import React, { useMemo, useState } from 'react';
import { clinicService } from '../../services/api';

const EXAMPLE_JSON = {
  clid: 'HKSH0006',
  mode: 'replace_listed',
  doctor_schedules: [
    {
      external_doctor_code: '11565',
      primary_name: 'DR. TSE, CHI TAT',
      is_default_schedule: true,
      active: true,
      schedule: {
        '0': [],
        '1': [{ from: '09:30', to: '12:50' }],
        '2': [],
        '3': [],
        '4': [{ from: '09:30', to: '12:50' }],
        '5': [],
        '6': [],
        PH: [],
        step: '00:15',
      },
    },
  ],
};

/**
 * Bulk export / configure doctor schedules via JSON for one clinic.
 */
const DoctorSchedulesJsonPanel = ({ clinicId, onImported }) => {
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [mode, setMode] = useState('replace_listed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const placeholder = useMemo(
    () => JSON.stringify({ ...EXAMPLE_JSON, clid: clinicId || EXAMPLE_JSON.clid }, null, 2),
    [clinicId]
  );

  const handleExport = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const data = await clinicService.exportDoctorSchedulesJson(clinicId);
      const text = JSON.stringify({ ...data, mode }, null, 2);
      setJsonText(text);

      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clinic_${clinicId}_doctor_schedules.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Exported doctor schedules JSON.');
      console.log('[DoctorSchedulesJsonPanel] export ok', data);
    } catch (err) {
      console.error('[DoctorSchedulesJsonPanel] export failed', err);
      setError(err.response?.data?.detail || err.message || 'Failed to export doctor schedules');
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        setJsonText(JSON.stringify(parsed, null, 2));
        if (parsed.mode) setMode(parsed.mode);
        setError(null);
        setSuccess(`Loaded ${file.name}`);
      } catch (err) {
        console.error('[DoctorSchedulesJsonPanel] invalid JSON file', err);
        setError('Invalid JSON file.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleImport = async () => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      setLastResult(null);

      const parsed = JSON.parse(jsonText);
      const doctorSchedules = parsed.doctor_schedules || parsed.clinic_doctor_schedules;
      if (!Array.isArray(doctorSchedules) || doctorSchedules.length === 0) {
        setError('JSON must include a non-empty doctor_schedules array.');
        return;
      }

      const payload = {
        mode: parsed.mode || mode,
        doctor_schedules: doctorSchedules,
      };

      if (
        !window.confirm(
          payload.mode === 'replace_listed'
            ? `Replace schedules for ${doctorSchedules.length} listed doctor row(s) in ${clinicId}? Existing schedules for those doctors will be deleted first.`
            : `Upsert ${doctorSchedules.length} schedule row(s) into ${clinicId}?`
        )
      ) {
        return;
      }

      const response = await clinicService.importDoctorSchedulesJson(clinicId, payload);
      setLastResult(response.result || null);
      setSuccess(response.message || 'Import completed.');
      console.log('[DoctorSchedulesJsonPanel] import ok', response);
      if (onImported) onImported(response);
    } catch (err) {
      console.error('[DoctorSchedulesJsonPanel] import failed', err);
      setError(err.response?.data?.detail || err.message || 'Failed to import doctor schedules');
    } finally {
      setBusy(false);
    }
  };

  const handleLoadExample = () => {
    setJsonText(placeholder);
    setMode('replace_listed');
    setError(null);
    setSuccess('Loaded example JSON (edit doctor identifiers before importing).');
  };

  return (
    <div className="mb-6 border rounded-lg bg-gray-50">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div>
          <p className="font-medium text-gray-800">JSON export / configure all</p>
          <p className="text-sm text-gray-500">
            Export every doctor schedule, or paste/upload JSON to configure schedules in one shot.
          </p>
        </div>
        <span className="text-sm text-blue-600">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t">
          <div className="flex flex-wrap gap-2 pt-3">
            <button
              type="button"
              onClick={handleExport}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            >
              Export JSON
            </button>
            <label className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-100 cursor-pointer">
              Upload JSON
              <input type="file" accept="application/json,.json" className="hidden" onChange={handleFileUpload} />
            </label>
            <button
              type="button"
              onClick={handleLoadExample}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-white border rounded hover:bg-gray-100 disabled:opacity-50"
            >
              Load example
            </button>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="px-2 py-1.5 text-sm border rounded bg-white"
              title="Import mode"
            >
              <option value="replace_listed">Mode: replace listed doctors</option>
              <option value="upsert">Mode: upsert by schedule id</option>
            </select>
            <button
              type="button"
              onClick={handleImport}
              disabled={busy || !jsonText.trim()}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? 'Working...' : 'Import / configure'}
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Days use Monday=0 … Sunday=6. Empty day arrays mean not on roster that day.
            Match doctors by <code>doctor_id</code>, <code>external_doctor_code</code>, or <code>primary_name</code>.
          </p>

          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder={placeholder}
            rows={16}
            className="w-full font-mono text-xs p-3 border rounded bg-white"
          />

          {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
          {success && <div className="p-3 bg-green-100 text-green-700 rounded text-sm">{success}</div>}
          {lastResult && (
            <pre className="p-3 bg-white border rounded text-xs overflow-auto max-h-48">
              {JSON.stringify(lastResult, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default DoctorSchedulesJsonPanel;
